import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import { FlexColumn } from '../styles/Containers'
import BodyText, { FormTitle, ErrorText, MedBodyText } from '../styles/Text'
import { Checkbox, FormControlLabel } from '../styles/Checkbox'
import FormInput from '../inputFields/FormInput'
import Tooltip from '../common/Tooltip'
import { theme } from '../../themes/theme'

const Red = styled.span`
  color: ${theme.colors.error};
`

const TitleWithTooltipContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const TitleWithoutMargin = styled(FormTitle)`
  margin: 0;
`

const TierContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`

const TierCard = styled.div`
  flex: 1;
  min-width: 280px;
  border: 2px solid ${props => (props.selected ? theme.colors.primary : 'transparent')};
  border-radius: 8px;
  padding: 20px;
  background-color: ${props => (props.selected ? theme.colors.primaryExtraLight : theme.colors.background.light)};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.colors.primary};
  }
`

const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const TierHeading = styled(BodyText)`
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 24px;
  line-height: 150%;
  letter-spacing: 0px;
  color: ${theme.colors.text.heading};
  margin: 0;
`

const TierName = styled(TierHeading)`
  text-align: left;
`

const TierPrice = styled(TierHeading)`
  text-align: right;
`

const TierDescription = styled(MedBodyText)`
  margin-bottom: 16px;
`

const TierListHeader = styled(MedBodyText)`
  font-weight: 600;
  margin-bottom: 8px;
`

const TierOptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 15px;
  margin-bottom: 6px;

  .MuiFormControlLabel-root {
    margin: 0;
    margin-right: 0;
  }

  .MuiCheckbox-root {
    padding: 0;
    margin-right: 5px;
  }
`

const CheckIcon = styled(CheckCircleIcon)`
  color: rgba(16, 208, 112, 1);
  font-size: 18px;
`

function RequestForm({ tierConfig = null, additionalDetailsEnabled = false, loading = false }) {
  const { register, watch, setValue, formState } = useFormContext()
  const selectedTier = watch('requestInformation.tier')
  const tierOptions = watch('requestInformation.tierOptions') || []
  const tierPricingEnabled = !!tierConfig?.enabled

  if (!tierPricingEnabled && !additionalDetailsEnabled) return null

  const isTierSelected = tierName => {
    if (!selectedTier || !tierName) return false
    return selectedTier.toLowerCase().trim() === tierName.toLowerCase().trim()
  }

  const handleTierSelect = tierName => {
    setValue('requestInformation.tier', tierName)
    setValue('requestInformation.tierOptions', [])
  }

  const handleTierOptionToggle = option => {
    const currentOptions = tierOptions || []
    const newOptions = currentOptions.includes(option) ? currentOptions.filter(opt => opt !== option) : [...currentOptions, option]
    setValue('requestInformation.tierOptions', newOptions)
  }

  const tierError = !!formState.errors.requestInformation?.tier
  const tierHelperText = formState.errors?.requestInformation?.tier?.message

  return (
    <FlexColumn>
      <FormTitle>Request Information</FormTitle>
      {tierPricingEnabled && (
        <BodyText>
          Please select which tier of request you would like. (Please read carefully before choosing an option)
          <Red> *</Red>
        </BodyText>
      )}
      <FlexColumn gap="20px">
        {tierPricingEnabled && (
          <>
            <TierContainer>
              {tierConfig.tierOptions.map(tier => (
                <TierCard key={tier.name} selected={isTierSelected(tier.name)} onClick={() => handleTierSelect(tier.name)}>
                  <TierHeader>
                    <TierName>{tier.heading || tier.name}</TierName>
                    <TierPrice>${tier.price} CAD</TierPrice>
                  </TierHeader>
                  <TierDescription>{tier.description}</TierDescription>

                  {tier.list && tier.list.length > 0 && !tier.editableList && (
                    <>
                      <TierListHeader>{tier.listHeader || 'Includes:'}</TierListHeader>
                      {tier.list.map((item, index) => (
                        <TierOptionItem key={index}>
                          <CheckIcon />
                          <MedBodyText>{item}</MedBodyText>
                        </TierOptionItem>
                      ))}
                    </>
                  )}

                  {tier.editableList && tier.listHeader && (
                    <>
                      <TierListHeader>{tier.listHeader}</TierListHeader>
                      {tier.list &&
                        tier.list.length > 0 &&
                        tier.list.map((option, index) => (
                          <TierOptionItem
                            key={index}
                            onClick={e => {
                              e.stopPropagation()
                              if (!isTierSelected(tier.name)) {
                                handleTierSelect(tier.name)
                              }
                            }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isTierSelected(tier.name) ? tierOptions.includes(option) : false}
                                  onChange={e => {
                                    e.stopPropagation()
                                    if (!isTierSelected(tier.name)) {
                                      handleTierSelect(tier.name)
                                    }
                                    handleTierOptionToggle(option)
                                  }}
                                  disabled={loading}
                                />
                              }
                              label={<MedBodyText>{option}</MedBodyText>}
                            />
                          </TierOptionItem>
                        ))}
                    </>
                  )}
                </TierCard>
              ))}
            </TierContainer>

            <input
              type="hidden"
              {...register('requestInformation.tier', {
                required: 'Please select a tier'
              })}
              value={selectedTier || ''}
            />
            {tierError && <ErrorText role="alert">{tierHelperText}</ErrorText>}
          </>
        )}

        {additionalDetailsEnabled && (
          <FlexColumn gap="10px">
            <TitleWithTooltipContainer>
              <TitleWithoutMargin>Additional Details</TitleWithoutMargin>
              <Tooltip info="Please provide any additional details or specifications for your request." />
            </TitleWithTooltipContainer>
            <FormInput id="requestInformation.additionalDetails" label="" multiline minRows={6} disabled={loading} />
          </FlexColumn>
        )}
      </FlexColumn>
    </FlexColumn>
  )
}

RequestForm.propTypes = {
  tierConfig: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    tierOptions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        heading: PropTypes.string,
        price: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired,
        editableList: PropTypes.bool,
        listHeader: PropTypes.string,
        list: PropTypes.arrayOf(PropTypes.string)
      })
    ).isRequired
  }),
  additionalDetailsEnabled: PropTypes.bool,
  loading: PropTypes.bool
}

export default RequestForm
